import useApiRequest from "@/lib/hooks/use-request";
import { formatDateTime } from "@/lib/hooks/utils";
import { SalesRankResponseDto } from "@/lib/interfaces/sales-ranks";
import PageNotFound from "@/page-components/error/page-not-found";
import { useBreadcrumb } from "@/ui-components/ui/breadcrumb/breadcrumb-context";
import { Button } from "@/ui-components/ui/button";
import ConfirmationDialog from "@/ui-components/ui/confirmation-dialog";
import DataFetchParent from "@/ui-components/ui/data-fetch";
import { Table, TableBody, TableCell, TableRow } from "@/ui-components/ui/table";
import { PageTitle, PageTitleContainer } from "@/ui-components/utils/menu-items/page-title";
import { Archive, ArchiveRestore, Edit2, X } from "lucide-react";
import { Dispatch, SetStateAction, useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import SalesRankEditor from "../sales-rank-editor";

const SalesRankItem = () => {
  const { id } = useParams();
  return <>{id ? <SalesRankItemFetcher id={id} /> : <PageNotFound />}</>;
};

const SalesRankItemFetcher = ({ id }: { id: string }) => {
  const { apiRequest } = useApiRequest();
  const [item, setItem] = useState<SalesRankResponseDto>();
  const [isLoading, setIsLoading] = useState(false);
  const [reload, setReload] = useState(false);

  const getItem = useCallback(async () => {
    setIsLoading(true);
    const response = await apiRequest<SalesRankResponseDto>(`settings/sales-ranks/${id}`, "GET");
    if (response.data) {
      setItem(response.data);
    }
    setIsLoading(false);
  }, [apiRequest, id]);

  useEffect(() => {
    getItem();
  }, [getItem, reload]);

  return (
    <DataFetchParent
      isLoading={isLoading}
      data={item}
      renderElement={(loadedItem: SalesRankResponseDto) => <SalesRankDetails item={loadedItem} setReload={setReload} />}
    />
  );
};

const SalesRankDetails = ({
  item,
  setReload,
}: {
  item: SalesRankResponseDto;
  setReload: Dispatch<SetStateAction<boolean>>;
}) => {
  const { setTitle } = useBreadcrumb();
  const [isEditing, setIsEditing] = useState(false);
  const { apiRequest } = useApiRequest();

  const toggleArchive = async () => {
    await apiRequest(`settings/sales-ranks/${item._id}/${item.isArchive ? "unarchive" : "archive"}`, "PATCH");
    setReload((prev) => !prev);
  };

  useEffect(() => {
    setTitle(item.title);
  }, [item, setTitle]);

  return (
    <div>
      <PageTitleContainer>
        <div className="flex flex-row justify-between items-center">
          <PageTitle>Details {item.title}</PageTitle>
          <div className="flex gap-2">
            <ConfirmationDialog
              onConfirm={toggleArchive}
              title="Verkaufsrang archivieren"
              description="Möchten Sie den Verkaufsrang wirklich archivieren?"
              variant={item.isArchive ? "default" : "destructive"}
            >
              <Button className="gap-2" variant={item.isArchive ? "default" : "destructive"}>
                <span>{item.isArchive ? "Wiederherstellen" : "Archivieren"}</span>
                {item.isArchive ? <ArchiveRestore size={16} /> : <Archive size={16} />}
              </Button>
            </ConfirmationDialog>
            <Button onClick={() => setIsEditing(!isEditing)} className="gap-2" variant="secondary">
              <span>{isEditing ? "Abbrechen" : "Bearbeiten"}</span>
              {isEditing ? <X size={16} /> : <Edit2 size={16} />}
            </Button>
          </div>
        </div>
      </PageTitleContainer>
      <div>
        {isEditing ? (
          <div className="p-4 w-1/2 items-center m-auto">
            <SalesRankEditor
              item={item}
              onSubmitDone={() => {
                setIsEditing(false);
                setReload((prev) => !prev);
              }}
            />
          </div>
        ) : (
          <div className="w-1/2 m-auto mt-8">
            <Table>
              <TableBody>
                <TableRow>
                  <TableCell className="font-medium">Titel</TableCell>
                  <TableCell>{item.title}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">Provision</TableCell>
                  <TableCell>{item.provision}%</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">Erstellt</TableCell>
                  <TableCell>{formatDateTime(item.createdAt)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">Zuletzt Aktualisiert</TableCell>
                  <TableCell>{formatDateTime(item.updatedAt)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">Verkäufe bis zum nächsten Rang</TableCell>
                  <TableCell>{item.salesUntilNextRank}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
};

export default SalesRankItem;
